/* eslint-disable react/no-unused-state */
import React from 'react';
import moment from 'moment';
import { HTTP_METHOD } from '@corporate-initiatives/ci-portal-js-sdk';

import {
  IActivateProjectModalWizardState,
  IActivateProjectModalWizardPage1Data,
  IActivateProjectModalWizardPage3Data,
  activateProjectModalWizardInitialState,
  IActivateProjectModalWizardPage2Data,
} from './activate-project-data.interface';
import { IModalWizardPageDataError } from '../../../types/modal/modal-wizard-page-data-error.interface';
import { IModalWizardPageData } from '../../../types/modal/modal-wizard-page-data.interface';
import { IProjectContactRecord } from '../../../types/project/project-contact.record.interface';
import { IProjectCompanyRecord } from '../../../types/project/project-company.record.interface';
import { IProjectFileRecord } from '../../../types/project/project-file.record.interface';
import { IProjectMaintenanceTypeRecord } from '../../../types/project/project-maintenance-type.record.interface';
import { IProjectOrderRecord } from '../../../types/project/project-order.record.interface';
import { IProjectRecord } from '../../../types/project/project.record.interface';
import { ConfirmActionModalResult } from '../../../types/modal/modal-result';
import { ModalProps } from '../../../types/modal/modal.props';

import { APIContextProps, connectToAPIProvider } from '../../providers/api-provider';

import { ModalWizard } from '../modal-wizard';
import { ActivateProjectModalPage1 } from './wizard-pages/activate-project-modal-page-1';
import { ActivateProjectModalPage3 } from './wizard-pages/activate-project-modal-page-3';
import { ActivateProjectModalPage4 } from './wizard-pages/activate-project-modal-page-4';
import { ActivateProjectModalPage5 } from './wizard-pages/activate-project-modal-page-5';
import { ActivateProjectModalPage6 } from './wizard-pages/activate-project-modal-page-6';

import { fileCompare } from '../../../helpers/file-compare.helper';
import { SERObject } from '../../../helpers/ser-object.helper';
import { apiAborter } from '../../../helpers/api-aborter.helper';
import { titleCase } from '../../../utils/title-case';

import { THEME_COLOR } from '../../../constants/theme-color.const';
import { MODAL_WIZARD_BUTTON_TYPE, A_MODAL_WIZARD_BUTTON_TYPE } from '../../../constants/modal-wizard-button-type.const';
import { ACTIVATE_PROJECT_MODAL_WIZARD_PAGE, AN_ACTIVATE_PROJECT_MODAL_WIZARD_PAGE } from './activate-project-modal-wizard-page.const';
import { PROJECT_FILE_TYPE, A_PROJECT_FILE_TYPE } from '../../../constants/project-file-type.const';
import { PROJECT_CONTACT_TYPE } from '../../../constants/project-contact-type.const';
import { PROJECT_COMPANY_TYPE } from '../../../constants/project-company-type.const';
import { DATE_FORMAT } from '../../../constants/date-format.const';
import { cancelFileUpload, confirmFileUpload, requestFileUpload, uploadFile } from '../../../helpers/file-upload.helper';
import { ActivateProjectModalPage2 } from './wizard-pages/activate-project-modal-page-2';

export type ActivateProjectModalWizardProps = ModalProps<ConfirmActionModalResult> & {
  apiProvider: APIContextProps,
  initialData: IProjectRecord,
  onModalComplete: (success: boolean) => unknown,
}

class ActivateProjectModalWizardComponent extends React.Component<ActivateProjectModalWizardProps, IActivateProjectModalWizardState> {
  private loadProjectOrderAbortController: null | AbortController;

  private findProjectInvoicingContactAbortController: null | AbortController;

  private findProjectBillingAgentCompanyAbortController: null | AbortController;

  private activateProjectAbortController: null | AbortController;

  private uploadProjectFileAbortController: Record<A_PROJECT_FILE_TYPE, null | AbortController>;

  /**
   * @constructor
   */
  constructor(props: ActivateProjectModalWizardProps) {
    super(props);
    this.state = activateProjectModalWizardInitialState;

    this.loadProjectOrderAbortController = null;
    this.findProjectInvoicingContactAbortController = null;
    this.findProjectBillingAgentCompanyAbortController = null;
    this.activateProjectAbortController = null;

    this.uploadProjectFileAbortController = {
      [PROJECT_FILE_TYPE.ORDER]: null,
      [PROJECT_FILE_TYPE.PROPOSAL]: null,
    };
  }


  /**
   * @inheritdoc
   */
  componentDidMount = () => {
    this.initialise();
  }


  /**
   * @inheritdoc
   */
  componentWillUnmount = () => {
    if (this.loadProjectOrderAbortController) this.loadProjectOrderAbortController.abort();
    if (this.findProjectInvoicingContactAbortController) this.findProjectInvoicingContactAbortController.abort();
    if (this.findProjectBillingAgentCompanyAbortController) this.findProjectBillingAgentCompanyAbortController.abort();
    if (this.activateProjectAbortController) this.activateProjectAbortController.abort();

    Object.values(PROJECT_FILE_TYPE).forEach((fileTypeId) => {
      if (this.uploadProjectFileAbortController[fileTypeId] !== null) {
        (this.uploadProjectFileAbortController[fileTypeId] as AbortController).abort();
        this.uploadProjectFileAbortController[fileTypeId] = null;
      }
    });
  }


  /**
   * When the wizard is loaded, we have to fetch some pre-existing information about the project
   */
  private initialise = async () => {
    // The initial data is a project record. Just use that
    const { initialData: projectRecord } = this.props;
    const { pageData } = this.state;

    this.setState({
      projectRecord,
    }, async () => {
      // Load a pre-existing project order (if one exists)
      let activatingProjectOrderRecord: IProjectOrderRecord | null = null;
      if (projectRecord.activation_order_id) {
        const loadProjectOrderResult = await this.loadProjectOrder(projectRecord.activation_order_id);
        if (loadProjectOrderResult.success) {
          activatingProjectOrderRecord = loadProjectOrderResult.result;
        }
      }

      // set the Maintenance type
      const projectMaintenanceTypeRecord: IProjectMaintenanceTypeRecord | null = projectRecord.project_maintenance_type || null;

      // Look for the invoicing project_contact
      const findInvoicingContactResult = await this.findProjectInvoicingContact();
      const invoicingContactRecord: IProjectContactRecord | null = findInvoicingContactResult.result;

      // Look for the Billing agent project_company
      const findBillingAgentResult = await this.findProjectBillingAgent();
      const billingAgentRecord: IProjectCompanyRecord | null = findBillingAgentResult.result;

      this.setState({
        activatingProjectOrderRecord,

        // Populate some of the page data from the project record
        pageData: {
          ...pageData,

          // Page 1
          [ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION]: {
            ...pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION],
            ...(activatingProjectOrderRecord ? {
              email_only_activation: /^Email [0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4}$/.test(activatingProjectOrderRecord.order_no ?? '') ? 1 : 0,
            } : {}),
          },

          // Page 2
          [ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_2_DTOOLS_AS_SOLD_PROJECT]: {
            ...pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_2_DTOOLS_AS_SOLD_PROJECT],
          },

          // Page 3
          [ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_3_REVIEW_INVOICE_INFORMATION]: {
            ...pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_3_REVIEW_INVOICE_INFORMATION],
            billing_address: projectRecord.billing_address,
            ...((billingAgentRecord && billingAgentRecord.company) ? {
              billing_agent_id: billingAgentRecord.company.id,
              billing_agent: {
                id: billingAgentRecord.company.id,
                name: billingAgentRecord.company.name,
              },
            } : {}),
            ...((invoicingContactRecord && invoicingContactRecord.person) ? {
              invoicing_contact_id: invoicingContactRecord.person.id,
              invoicing_contact: {
                id: invoicingContactRecord.person.id,
                first: invoicingContactRecord.person.first,
                middle: invoicingContactRecord.person.middle ?? null,
                last: invoicingContactRecord.person.last,
              },
              notify_invoicing_contact: invoicingContactRecord.send_notifications,
            } : {}),
            ...((projectMaintenanceTypeRecord && projectMaintenanceTypeRecord.id) ? {
              project_maintenance_type_id: projectRecord.project_maintenance_type_id,
              project_maintenance_type: {
                id: projectMaintenanceTypeRecord.id,
                name: titleCase(projectMaintenanceTypeRecord.name),
              },
            } : {}),
          },

          // Page 4
          [ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_4_CHECKLIST_ITEMS]: {
            ...pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_4_CHECKLIST_ITEMS],
            po_saved_in_project_drive: projectRecord.po_saved_in_project_drive,
            as_sold_proposal_saved: projectRecord.as_sold_proposal_saved,
            po_value_matches_as_sold: projectRecord.po_value_matches_as_sold,
            finance_reports_created: projectRecord.finance_reports_created,
          },

          // Page 5
          [ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_5_SEND_NOTIFICATIONS]: {
            ...pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_5_SEND_NOTIFICATIONS],
            project_activation_team_note: '',
          },
        },
      }, async () => {
        // TODO: more initialisation
        // Initialisation complete
        this.setState({
          initialising: false,
        });
      });
    });
  }


  /**
   * Performs all of the actions which actually represent the project activation
   *
   * For the moment I can't think of a better way to process this sequential data in the time allotted.... yuk
   */
  private processProjectActivation = async () => {
    // Determine which files need uploading
    const { pageData, activatingPoDocument, asSoldProposalDocument } = this.state;
    let { uploadActivatingPODocumentResult, uploadAsSoldProposalDocumentResult } = this.state;
    const activatingPODocumentRequiresUpload = (
      (!!pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION].activating_po_document_file && !activatingPoDocument) ||
      (uploadActivatingPODocumentResult && !uploadActivatingPODocumentResult.success)
    );
    const asSoldProposalDocumentRequiresUpload = (
      (!!pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION].as_sold_proposal_document_file && !asSoldProposalDocument) ||
      (uploadAsSoldProposalDocumentResult && !uploadAsSoldProposalDocumentResult.success)
    );

    // Reset the state of the activation data
    this.setState({
      processing: true,
      success: false,
      uploadActivatingPODocumentResult: activatingPODocumentRequiresUpload ? null : uploadActivatingPODocumentResult,
      uploadAsSoldProposalDocumentResult: asSoldProposalDocumentRequiresUpload ? null : uploadAsSoldProposalDocumentResult,
    }, async () => {
      // Perform the Upload of the Activating Purchase Order Project File (if required)
      if (activatingPODocumentRequiresUpload) {
        uploadActivatingPODocumentResult = await this.uploadActivatingPODocument();
      }

      // Record the Result
      this.setState({
        uploadActivatingPODocumentResult,
        activatingPoDocument: uploadActivatingPODocumentResult?.result ?? null,
      }, async () => {
        // Proceed if the upload was successful (or not required)
        if (!activatingPODocumentRequiresUpload || uploadActivatingPODocumentResult?.success) {
          // Perform the Upload of the As SOld Proposal Document (if required)
          if (asSoldProposalDocumentRequiresUpload) {
            uploadAsSoldProposalDocumentResult = await this.uploadAsSoldProposalDocument();
          }

          // Record the Result
          this.setState({
            uploadAsSoldProposalDocumentResult,
            asSoldProposalDocument: uploadAsSoldProposalDocumentResult?.result ?? null,
          }, async () => {
            // Proceed if the upload was successful
            if (!asSoldProposalDocumentRequiresUpload || uploadAsSoldProposalDocumentResult?.success) {
              // Execute the Activate Project action
              const activateProjectResult = await this.executeActivateProjectAction();
              const { projectRecord } = this.state;

              // Record the result
              this.setState({
                activateProjectResult,
                projectRecord: activateProjectResult.success ? activateProjectResult.result : projectRecord,
              }, async () => {
                // If the project activation was successful, the modal can be closed.
                if (activateProjectResult.success) {
                  // Processing Complete
                  this.setState({
                    processing: false,
                    success: true,
                    isDirty: false,
                  });
                } else {
                  // Update Project failed
                  this.setState({ processing: false, success: false });
                }
              });
            } else {
              // Upload of As Sold Proposal Document failed
              this.setState({ processing: false, success: false });
            }
          });
        } else {
          // Upload of Activating PO Document failed
          this.setState({ processing: false, success: false });
        }
      });
    });
  }


  /**
   * Fired by the wizard when the user attempts to progress forward.
   *
   * Returns true if the page validated successfully otherwise it returns an array of data errors
   */
  private handleValidatePageData = (pageIndex: number, pageData: IModalWizardPageData): true | IModalWizardPageDataError[] => {
    const dataErrors: IModalWizardPageDataError[] = [];

    switch (pageIndex as AN_ACTIVATE_PROJECT_MODAL_WIZARD_PAGE) {
      //
      // Page 1 - Activating Purchase Order Information
      case ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION: {
        const typedPageData = pageData as IActivateProjectModalWizardPage1Data;
        if (!typedPageData.email_only_activation && !typedPageData.order_no) {
          dataErrors.push({
            fieldName: 'order_no',
            error: 'You must provide a reference Order number from the Client\'s Purchase Order.',
          });
        }
        if (!typedPageData.order_total && !typedPageData.email_only_activation) {
          dataErrors.push({
            fieldName: 'order_total',
            error: 'The order value must be greater than $0.00.',
          });
        }
        if (!typedPageData.received_date) {
          dataErrors.push({
            fieldName: 'received_date',
            error: 'The order received date is required.',
          });
        }
        if (!typedPageData.activating_po_document_file) {
          dataErrors.push({
            fieldName: 'activating_po_document_file',
            error: 'You must upload a PDF or scan of the customer Purchase Order.',
          });
        }
        break;
      }

      // Page 2 - As Sold Project Details
      case ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_2_DTOOLS_AS_SOLD_PROJECT: {
        const typedPageData = pageData as IActivateProjectModalWizardPage2Data;

        // if there's no D-Tools ID and either cost or price is also missing:
        if (typedPageData.eligible_for_commission && !typedPageData.dtools_project_id && (!typedPageData.as_sold_cost || !typedPageData.as_sold_price)) {
          dataErrors.push({
            fieldName: 'dtools_project_id',
            error: 'Either a D-Tools Project Link, or As Sold Cost and Price information are required to continue.',
          });
          if (!typedPageData.as_sold_cost) {
            dataErrors.push({
              fieldName: 'as_sold_cost',
              error: 'The "As Sold Cost" is required to continue.',
            });
          }
          if (!typedPageData.dtools_project_id) {
            if (!typedPageData.as_sold_price) {
              dataErrors.push({
                fieldName: 'as_sold_price',
                error: 'The "As Sold Price" is required to continue.',
              });
            }
          }
        }
        break;
      }


      // Page 4 - Checklist Items
      case ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_4_CHECKLIST_ITEMS: {
        const typedPageData = pageData as IActivateProjectModalWizardPage3Data;
        if (!typedPageData.po_saved_in_project_drive) {
          dataErrors.push({
            fieldName: 'po_saved_in_project_drive',
            error: 'Please indicate that you have also uploaded the Purchase Order to the Project Drive.',
          });
        }
        break;
      }
    }

    return (dataErrors.length > 0) ? dataErrors : true;
  };


  /**
   * Fired by the wizard when the user changes any of the page data and just before the state is updated.
   * This gives us a chance to hook into any of the data changes and make additional changes prior to being committed to the state.
   *
   * Return PageData with alterations to augment / change / hook the change of page data
   * Return null to leave the pageData unchanged.
   */
  private handleBeforePageDataChanged = (pageIndex: number, pageData: IModalWizardPageData): null | IModalWizardPageData => {
    switch (pageIndex as AN_ACTIVATE_PROJECT_MODAL_WIZARD_PAGE) {
      // Page 1
      case ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION: {
        const { pageData: oldPageData } = this.state;
        const typedPageData = pageData as IActivateProjectModalWizardPage1Data;
        const oldPage1Data = oldPageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION];

        // The activation is an email_only_activation and the user changed the email_only_activation or the received date
        // Update the auto-generated order number
        if (
          (typedPageData.email_only_activation && (
            (typedPageData.email_only_activation !== oldPage1Data.email_only_activation) ||
            (typedPageData.received_date !== oldPage1Data.received_date)
          ))
        ) {
          typedPageData.order_no = `Email ${
            typedPageData.received_date
              ? moment(typedPageData.received_date, DATE_FORMAT.YEAR_MONTH_DAY_DASHES, true).format(DATE_FORMAT.DAY_MONTH_YEAR_SLASHES)
              : ''
          }`;
        }

        // Has the user selected a new Activating PO Document File?
        if (!fileCompare(typedPageData.activating_po_document_file, oldPage1Data.activating_po_document_file)) {
          // Reset the activating PO Document (outside of this state update)
          setImmediate(() => this.setState({
            activatingPoDocument: null,
            activatingPoDocument_uploadStatus: 0,
          }));
        }

        // Has the user selected a new As Sold Proposal File?
        if (!fileCompare(typedPageData.as_sold_proposal_document_file, oldPage1Data.activating_po_document_file)) {
          // Reset the Proposal Document (outside of this state update)
          setImmediate(() => this.setState({
            asSoldProposalDocument: null,
            asSoldProposalDocument_uploadStatus: 0,
          }));
        }

        return typedPageData;
      }

      // Page 3
      case ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_3_REVIEW_INVOICE_INFORMATION: {
        const typedPageData = pageData as IActivateProjectModalWizardPage2Data;
        return {
          ...typedPageData,

          // Update the ID of the billing agent to match the selection from the company select input
          billing_agent_id: typedPageData.billing_agent ? typedPageData.billing_agent.id : null,

          // Update the ID of the invoicing contact to match the selection from the contact select input
          invoicing_contact_id: typedPageData.invoicing_contact ? typedPageData.invoicing_contact.id : null,

          // Reset the notify_invoicing_contact flag if the user has cleared the invoicing contact
          notify_invoicing_contact: typedPageData.invoicing_contact ? typedPageData.notify_invoicing_contact : 0,

          // Update the maintenance type to match the selection, or make it blank
          project_maintenance_type_id: typedPageData.project_maintenance_type ? typedPageData.project_maintenance_type.id : null,
        };
      }
    }

    return null;
  };


  /**
   * Ensure the right buttons are available at the right time
   */
  private handleCheckButtonVisible = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number, defaultButtonVisible: boolean) => {
    const { processing, success } = this.state;

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.CANCEL) {
      // Don't show the cancel button on the last page unless processing failed
      return (
        (pageIndex < ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_6_PROCESSING)
        ||
        (
          (pageIndex === ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_6_PROCESSING) &&
          (processing === false) &&
          (success === false)
        )
      );
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.SUBMIT) {
      // Only show the submit button on the second last page prior to processing
      return pageIndex === ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_5_SEND_NOTIFICATIONS;
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) {
      // Don't show the next button on the second last or last pages
      return pageIndex < ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_5_SEND_NOTIFICATIONS;
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.PREV) {
      // Don't show the previous button on the last page unless processing failed
      return (
        (
          (pageIndex > ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION) &&
          (pageIndex < ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_6_PROCESSING)
        )
        ||
        (
          (pageIndex === ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_6_PROCESSING) &&
          (processing === false) &&
          (success === false)
        )
      );
    }

    // use the default visibility for all other situations / button types
    return defaultButtonVisible;
  };


  /**
   * Ensure buttons are enabled at the appropriate time
   */
  private handleCheckButtonEnabled = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number, defaultButtonEnabled: boolean) => {
    const { initialising, projectRecord } = this.state;

    // Disable all but the cancel button while initialising
    if (initialising && (buttonType !== MODAL_WIZARD_BUTTON_TYPE.CANCEL)) return false;

    // Disable the "next" button on Page 1 if the project drive doesn't exist
    if (
      (pageIndex === ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION) &&
      (buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) &&
      (!projectRecord?.ms_group_drive_item_id)
    ) {
      return false;
    }

    return defaultButtonEnabled;
  }


  /**
   * Handle the click of one of the wizard buttons
   */
  private handleButtonClick = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number): boolean => {
    // If the submit button was clicked, begin processing the activation
    if ((buttonType === MODAL_WIZARD_BUTTON_TYPE.SUBMIT) && (pageIndex === ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_5_SEND_NOTIFICATIONS)) {
      // move to the last page
      this.setState({
        currentPageIndex: ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_6_PROCESSING,
      },
      // kick off the processing
      this.processProjectActivation);

      // Let the wizard know that the button click was handled
      return true;
    }

    // Returning false indicates that the button click was not handled.
    return false;
  };


  /**
   * Loads a pre-existing project order that has been used for activation in the past into the wizard
   */
  private loadProjectOrder = async (projectOrderId: number): Promise<SERObject<IProjectOrderRecord>> => {
    const result = new SERObject<IProjectOrderRecord>(false);

    const {
      apiProvider: { apiFetch },
      initialData: {
        id: project_id,
      },
    } = this.props;

    if (this.loadProjectOrderAbortController) this.loadProjectOrderAbortController.abort();

    this.loadProjectOrderAbortController = apiAborter();

    const response = await apiFetch(
      `/project/${project_id}/order/${projectOrderId}`,
      {
        name: 'ActivateProjectModalWizard::loadProjectOrder',
        signal: this.loadProjectOrderAbortController.signal,
      },
    );

    if (response.success) {
      this.loadProjectOrderAbortController = null;
      result.result = response.body;
      result.success = true;
    } else if (!response.aborted) {
      this.loadProjectOrderAbortController = null;
      console.error('Failed to load Project Order: ', response.error, response.body);
      result.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return result;
  }


  /**
   * Finds a pre-existing project contact of type "Invoicing Contact" that is already assigned to the project
   */
  private findProjectInvoicingContact = async (): Promise<SERObject<IProjectContactRecord>> => {
    const result = new SERObject<IProjectContactRecord>(false);

    const {
      apiProvider: { apiFetch },
      initialData: {
        id: project_id,
      },
    } = this.props;

    if (this.findProjectInvoicingContactAbortController) this.findProjectInvoicingContactAbortController.abort();

    this.findProjectInvoicingContactAbortController = apiAborter();

    const response = await apiFetch([
      `/project/${project_id}/contact?with[]=person`,
      'filter[0][field]=type_id',
      'filter[0][operation]=equals',
      `filter[0][value]=${PROJECT_CONTACT_TYPE.INVOICING_CONTACT}`,
    ].join('&'),
    {
      name: 'ActivateProjectModalWizard::findProjectInvoicingContact',
      signal: this.findProjectInvoicingContactAbortController.signal,
    });

    if (response.success) {
      this.findProjectInvoicingContactAbortController = null;
      if (response.body.data && response.body.data.length > 0) {
        result.result = response.body.data[0];
        result.success = true;
      }
    } else if (!response.aborted) {
      this.findProjectInvoicingContactAbortController = null;
      console.error('Failed to execute a search for a Project Invoicing Contact: ', response.error, response.body);
      result.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return result;
  }


  /**
   * Finds a pre-existing project company of type "Billing Agent" that is already assigned to the project
   */
  private findProjectBillingAgent = async (): Promise<SERObject<IProjectCompanyRecord>> => {
    const result = new SERObject<IProjectCompanyRecord>(false);

    const {
      apiProvider: { apiFetch },
      initialData: {
        id: project_id,
      },
    } = this.props;

    if (this.findProjectBillingAgentCompanyAbortController) this.findProjectBillingAgentCompanyAbortController.abort();

    this.findProjectBillingAgentCompanyAbortController = apiAborter();

    const response = await apiFetch([
      `/project/${project_id}/company?with[]=company`,
      'filter[0][field]=type_id',
      'filter[0][operation]=equals',
      `filter[0][value]=${PROJECT_COMPANY_TYPE.BILLING_AGENT}`,
    ].join('&'),
    {
      name: 'ActivateProjectModalWizard::findProjectBillingAgent',
      signal: this.findProjectBillingAgentCompanyAbortController.signal,
    });

    if (response.success) {
      this.findProjectBillingAgentCompanyAbortController = null;
      if (response.body.data && response.body.data.length > 0) {
        result.result = response.body.data[0];
        result.success = true;
      }
    } else if (!response.aborted) {
      this.findProjectBillingAgentCompanyAbortController = null;
      console.error('Failed to execute a search for a Project Billing Agent Company: ', response.error, response.body);
      result.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return result;
  }


  /**
   * Call the Activate Project action
   */
  private executeActivateProjectAction = async () => {
    const activateProjectResult = new SERObject<IProjectRecord>(false);

    const {
      pageData,
      activatingPoDocument,
      asSoldProposalDocument,
    } = this.state;

    const {
      apiProvider: { apiFetch },
      initialData: { id: project_id },
    } = this.props;

    let abortController = this.activateProjectAbortController;
    if (abortController) {
      abortController.abort();
    }
    abortController = apiAborter();
    this.activateProjectAbortController = abortController;

    // Get data from Page 1
    const {
      email_only_activation,
      order_total,
      order_no,
      received_date,
      comment,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION];

    // Get data from Page 2
    const {
      eligible_for_commission,
      dtools_project_id,
      as_sold_cost,
      as_sold_price,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_2_DTOOLS_AS_SOLD_PROJECT];

    // Get data from Page 3
    const {
      billing_address,
      billing_agent_id,
      invoicing_contact_id,
      notify_invoicing_contact,
      project_maintenance_type_id,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_3_REVIEW_INVOICE_INFORMATION];

    // Get data from Page 4
    const {
      po_saved_in_project_drive,
      as_sold_proposal_saved,
      finance_reports_created,
      po_value_matches_as_sold,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_4_CHECKLIST_ITEMS];

    // Get data from Page 5
    const {
      project_activation_team_note,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_5_SEND_NOTIFICATIONS];

    const response = await apiFetch(
      `/project/${project_id}/action/activate`,
      {
        name: 'ActivateProjectModalWizard::executeActivateProjectAction',
        method: HTTP_METHOD.POST,
        signal: abortController.signal,
        body: {
          order_total,
          comment,
          order_no,
          received: received_date,
          activating_purchase_order_project_file_id: activatingPoDocument?.id ?? null,
          proposal_project_file_id: asSoldProposalDocument?.id ?? null,
          email_only_activation,
          po_saved_in_project_drive,
          po_value_matches_as_sold,
          as_sold_proposal_saved,
          dtools_project_id,
          eligible_for_commission,
          as_sold_cost,
          as_sold_price,
          finance_reports_created,
          billing_address,
          billing_agent_id,
          invoicing_contact_id,
          notify_invoicing_contact,
          project_maintenance_type_id,
          project_activation_team_note,
        },
      },
    );

    if (response.success) {
      activateProjectResult.result = response.body.data;
      activateProjectResult.success = true;
    } else {
      console.error('Failed to create Project Order: ', response.error, response.body);
      activateProjectResult.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return activateProjectResult;
  }


  /**
   * Upload the specified activating PO document
   */
  private uploadActivatingPODocument = async () => {
    const result = new SERObject<IProjectFileRecord>(false);

    const fileTypeId = PROJECT_FILE_TYPE.ORDER;

    const {
      pageData,
    } = this.state;

    const {
      apiProvider: { apiFetch },
      initialData: {
        id: project_id,
      },
    } = this.props;

    // Get data from Page 1
    const {
      activating_po_document_file,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION];

    // Bail if the Activating PO Document has not been selected
    if (!activating_po_document_file) {
      // Nothing to Upload, return success.
      result.success = true;
      result.result = null;
      return result;
    }

    // Prepare a callback for updating the upload progress
    const onUploadProgress = (progress: number) => {
      this.setState({
        activatingPoDocument_uploadStatus: progress,
      });
    };

    // Request the Upload
    const requestUploadResult = await requestFileUpload<IProjectFileRecord>(
      `/project/${project_id}/file`,
      apiFetch,
      this.uploadProjectFileAbortController[fileTypeId],
      (updatedAbortController: null | AbortController) => {
        this.uploadProjectFileAbortController[fileTypeId] = updatedAbortController;
      },
      activating_po_document_file,
      {
        file_type_id: fileTypeId,
      },
    );
    if (requestUploadResult.success && requestUploadResult.result) {
      const { transport, actions } = requestUploadResult.result;

      // Upload the File
      const uploadResult = await uploadFile(
        activating_po_document_file,
        transport,
        onUploadProgress,
      );
      if (uploadResult.success) {
        // Confirm the file upload
        const confirmUploadResult = await confirmFileUpload<IProjectFileRecord>(apiFetch, actions.confirm);
        if (confirmUploadResult.success) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          result.result = confirmUploadResult.result!.data;
          result.success = true;
        } else {
          // Failed to transport the file
          result.error = confirmUploadResult.error;
        }
      } else {
        // cancel the file upload (don't wait)
        cancelFileUpload<IProjectFileRecord>(apiFetch, actions.cancel);

        // Failed to transport the file
        result.error = uploadResult.error;
      }
    } else {
      // Failed to request the upload
      result.error = requestUploadResult.error;
    }

    return result;
  }


  /**
   * Upload the specified As Sold Proposal document
   */
  private uploadAsSoldProposalDocument = async () => {
    const result = new SERObject<IProjectFileRecord>(false);

    const fileTypeId = PROJECT_FILE_TYPE.PROPOSAL;

    const {
      pageData,
    } = this.state;

    const {
      apiProvider: { apiFetch },
      initialData: {
        id: project_id,
      },
    } = this.props;

    // Get data from Page 1
    const {
      as_sold_proposal_document_file,
    } = pageData[ACTIVATE_PROJECT_MODAL_WIZARD_PAGE.PAGE_1_PURCHASE_ORDER_INFORMATION];

    // Bail if the As Sold Proposal Document has not been selected
    if (!as_sold_proposal_document_file) {
      // Nothing to Upload, return success.
      result.success = true;
      result.result = null;
      return result;
    }

    // Prepare a callback for updating the upload progress
    const onUploadProgress = (progress: number) => {
      this.setState({
        asSoldProposalDocument_uploadStatus: progress,
      });
    };

    // Request the Image Upload
    const requestUploadResult = await requestFileUpload(
      `/project/${project_id}/file`,
      apiFetch,
      this.uploadProjectFileAbortController[fileTypeId],
      (updatedAbortController: null | AbortController) => {
        this.uploadProjectFileAbortController[fileTypeId] = updatedAbortController;
      },
      as_sold_proposal_document_file,
      {
        file_type_id: fileTypeId,
      },
    );
    if (requestUploadResult.success && requestUploadResult.result) {
      const { transport, actions } = requestUploadResult.result;

      // Upload the File
      const uploadResult = await uploadFile(
        as_sold_proposal_document_file,
        transport,
        onUploadProgress,
      );
      if (uploadResult.success) {
        // Confirm the file upload
        const confirmUploadResult = await confirmFileUpload<IProjectFileRecord>(apiFetch, actions.confirm);
        if (confirmUploadResult.success) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          result.result = confirmUploadResult.result!.data;
          result.success = true;
        } else {
          // Failed to transport the file
          result.error = confirmUploadResult.error;
        }
      } else {
        // cancel the file upload (don't wait)
        cancelFileUpload<IProjectFileRecord>(apiFetch, actions.cancel);

        // Failed to transport the file
        result.error = uploadResult.error;
      }
    } else {
      // Failed to request the upload
      result.error = requestUploadResult.error;
    }

    return result;
  }


  /**
   * @inheritdoc
   */
  public render = (): JSX.Element => {
    const {
      initialData: {
        project_number,
      },
      id: modal_id,
      closeModal,
      onModalComplete,
    } = this.props;

    const {
      isDirty,
      currentPageIndex,
    } = this.state;

    return (
      <ModalWizard<IActivateProjectModalWizardState, ConfirmActionModalResult>
        {...this.props}
        color={THEME_COLOR.PRIMARY}
        size="lg"
        title={`Activate Project ${project_number}`}
        allowDismiss={!isDirty && currentPageIndex === 0}
        isDirty={isDirty || currentPageIndex > 0}
        pageComponents={[
          ActivateProjectModalPage1,
          ActivateProjectModalPage2,
          ActivateProjectModalPage3,
          ActivateProjectModalPage4,
          ActivateProjectModalPage5,
          ActivateProjectModalPage6,
        ]}
        wizardState={this.state}
        setWizardState={(newState: unknown, callback?: () => void) => {
          this.setState(newState as IActivateProjectModalWizardState, callback);
        }}
        onValidatePageData={this.handleValidatePageData}
        onPageIndexChanged={() => this.setState({ isDirty: true })}
        onPageDataChanged={() => this.setState({ isDirty: true })}
        onBeforePageDataChanged={this.handleBeforePageDataChanged}
        onModalCancel={() => closeModal(modal_id, isDirty)}
        onModalComplete={() => onModalComplete(true)}
        onCheckButtonVisible={this.handleCheckButtonVisible}
        onCheckButtonEnabled={this.handleCheckButtonEnabled}
        onButtonClick={this.handleButtonClick}
      />
    );
  }
}


// Wrap the Activate Project Modal Wizard in an API Provider
export const ActivateProjectModalWizard = connectToAPIProvider(ActivateProjectModalWizardComponent);
